/**
 * Quantity input plus quantity-UOM dropdown for one line, with optional adjustment of the typed
 * quantity to a whole number of pieces and a "Values have been updated" popper when it adjusts.
 * Despite its folder, it is not quick-entry only: it is this repo's counterpart of `QuantityUOM`
 * in `@ryerson/frontend.quantity-uom-inputs`, which the catalog and product-details screens use.
 *
 * Used by `../../QuickEntryV2.tsx`'s views (`QuickEntryDesktopV2.tsx`, `QuickEntryMobileV2.tsx`),
 * `ShoppingCart/ShoppingCartTable/ShoppingCartTableQuantityUomCell.tsx`,
 * `ShoppingListDetails/Helpers/ShoppingListDetailsTableComponents/TableQuantityAndQuantityUomControlCell.tsx`
 * and `QuoteDetails/Helpers/RowQuantityAndQuantityUomData.tsx`.
 *
 * Modes, chosen by props:
 * - Default (cart and list tables): the typed value settles for `debounceTime` (2 s) through
 *   `useDebouncerPlus`; then, with `recalculateQuantities`, it is adjusted by
 *   `updatedQuantityBasedOnQuantityUom` (`@ryerson/frontend.common`, rounding to the next
 *   whole-piece quantity), the popper shows, and `handleChange` gets the result.
 * - `disableLocalCalculation` (quote draft table): after the debounce, `handleChange` gets the
 *   typed value unchanged.
 * - `forceTrigger` (quick entry): every change is validated and reported through
 *   `handleValidationResponse` at once, and a non-zero value is passed to `handleChange`; an empty
 *   or zero value is not. The debounce effect is skipped.
 * - `hasUserChangedValue`: a settled value counts as a customer change even without a keystroke.
 *
 * Rules a change must keep:
 * - Empty or zero is an error. It is reported through `handleValidationResponse(hasError,
 *   itemData.id)` on mount and whenever the displayed quantity changes. Quick entry blocks Add
 *   Below on it; `QuoteDetails/Context/QuoteDetailsContext.tsx` → `handleValidationResponse`
 *   records it per row.
 * - `id` must be unique among the instances on a page: it builds the DOM ids
 *   `quantity-uom-input-<id>` and `quantity-uom-dropdown-<id>`, the default test id
 *   `number-input-<id>`, and filters pub-sub messages.
 * - In the non-`forceTrigger` path the value is clamped to `inputConstraint` (default 0 to 99,999);
 *   `forceTriggerChangeHandler` does not clamp.
 *
 * Pub-sub (`stateMessenger`; protocol in `components/Hooks/useDebouncerPlus.ts`): receives
 * `to-debounce` for its `id` from the neighbouring price-UOM control. When the customer leaves that
 * control it starts a deferred check (`useCheckValueInterval` from `@ryerson/frontend.common`,
 * 3 ticks of `debounceTime / 3`), publishes `is-debouncing` true/false for its `id`, and at the end
 * recalculates and calls `handleChange` if the input has not changed meanwhile.
 */
import React from "react";
import { Dropdown, NumberInput } from "@ryerson/frontend.form-controls";
import { LanguageContent } from "@ryerson/frontend.common";
import { Flex, FlexItem } from "@ryerson/frontend.layout";
import { Typography } from "@ryerson/frontend.typography";
import { Alert } from "@ryerson/frontend.notification";
import {
	Popper,
	useDebouncerPlus,
	useCheckValueInterval,
	updatedQuantityBasedOnQuantityUom,
} from "@ryerson/frontend.common";

import { useTheme } from "@ryerson/frontend.theme";
import styled from "@emotion/styled";
import { UOMValue } from "@ryerson/common.uom";
import { ControlType, ControlSize } from "@ryerson/frontend.form-controls";
import { useApplication } from "@ryerson/frontend.application";
import { CONSTANTS } from "@ryerson/frontend.common";
import { Subscribable } from "@ryerson/frontend.common";
import { Option } from "@ryerson/frontend.form-controls";

interface NumberConstrain {
	minimum: number;
	maximum: number;
}

/** Props of {@link QuantityUOMV2}; modes are described in the file header. */
interface QuantityUOMProps {
	DropDownLabelAddition?: JSX.Element | string;
	InputLabelAddition?: JSX.Element | string;
	id: number | string; // can be a row index if in a table
	itemData:
		| {
				materialLengthMin: number;
				materialWidth: number;
				id: string;
		  }
		| any;
	quantityUomOptions: Option[];
	handleChange: (quantityValue: number | undefined, uomValue: string) => void;
	handleUOMChange?: (uomValue: string) => void;

	// PUB SUB option to receive message from sibling components
	stateMessenger?: Subscribable<string>;

	quantityValue: number | undefined;
	uomValue: UOMValue;
	/** When true, validation runs on blur and on change, and when it turns true. */
	isSubmitted?: boolean;
	disabled?: boolean;
	/** Overrides `disabled` for the quantity input only. */
	disabledQuantity?: boolean;
	type?: ControlType;
	size?: ControlSize;
	showLabel?: boolean;
	quantityInputWidth?: number | string;
	uomInputWidth?: number | string;
	popperWidth?: number;
	isMobile?: boolean;
	/** Clamp range for typed values outside `forceTrigger` mode. */
	inputConstraint?: NumberConstrain;
	/** Milliseconds; also sets the deferred-check tick (`debounceTime / 3`). */
	debounceTime?: number;
	popperDisplayTime?: number;
	inputDataTestId?: string;
	dropdownDataTestId?: string;
	dataTestId?: string;
	/** Focuses the quantity input when it turns true after mount. */
	isFocused?: boolean;
	/** Receives `hasError` (empty or zero) and `itemData.id`, not `id`. */
	handleValidationResponse?: (hasError: boolean, id: number | string) => void;
	onDropdownOpen?: (isOpen: boolean) => void;
	/** Adjust a settled value to whole pieces. Default true. */
	recalculateQuantities?: boolean;
	quantityInputRef?: React.RefObject<HTMLInputElement>;
	/** Validate and report every change immediately; skips the debounce effect. */
	forceTrigger?: boolean;
	/** Treat a settled value as a customer change even without a keystroke. */
	hasUserChangedValue?: boolean;
	/** Skip adjustment and pass the settled value to `handleChange` unchanged. */
	disableLocalCalculation?: boolean;
}
/** The `document`, available only after mount (null during the static build's render). */
const useDocument = () => {
	const [localDocument, setLocalDocument] = React.useState<Document | null>(null);

	React.useEffect(() => {
		setLocalDocument(document);
	}, []);

	return localDocument;
};

/**
 * Quantity + quantity-UOM control for one line. `handleChange(quantity, uom)` fires with the
 * settled (and, by default, adjusted) quantity; `handleUOMChange` fires on every UOM pick. See the
 * file header for modes and rules.
 */
export const QuantityUOMV2: React.FC<QuantityUOMProps> = ({
	id,
	itemData,
	isMobile = false,
	uomValue,
	quantityValue,
	quantityInputWidth = 66,
	uomInputWidth = 77,
	popperWidth = 250,
	type = "primary",
	size,
	showLabel = false,
	disabled = false,
	disabledQuantity,
	isSubmitted = false,
	handleChange,
	handleValidationResponse,
	inputConstraint = { minimum: 0, maximum: 99999 },
	debounceTime = 2000,
	popperDisplayTime = 2000,
	inputDataTestId,
	dropdownDataTestId,
	dataTestId = "",
	isFocused = false,
	stateMessenger,
	quantityUomOptions = [],
	DropDownLabelAddition,
	InputLabelAddition,
	handleUOMChange,
	onDropdownOpen,
	recalculateQuantities = true,
	quantityInputRef,
	forceTrigger,
	hasUserChangedValue = false,
	disableLocalCalculation = false,
}) => {
	!size && (isMobile ? (size = "sm") : (size = "md"));
	const { theme } = useTheme();
	const {
		localization: { language },
	} = useApplication();

	const content = withLanguage[language || "en"];
	const { white, lightGray, darkGray, text } = theme.colors.primary || {};

	const [quantityUom, setQuantityUom] = React.useState<string>(uomValue);
	const [isPopperDisplayed, setIsPopperDisplayed] = React.useState<boolean>(false);
	const [isMounted, setHasMounted] = React.useState<boolean>(false);
	const [displayTrigger, setDisplayTrigger] = React.useState<boolean>(false);
	const [defferChangeTrigger, setDefferChangeTrigger] = React.useState<boolean>(false);

	let numberInputRef = React.useRef<Element | null | undefined>();
	let isError = React.useRef<boolean>(false);
	// Set by `recalculateQuantity` for a non-zero value, cleared when the customer types. While
	// set, the popper may show, and the value is not debounced, validated or adjusted again as if
	// it were typed.
	let isRecalculate = React.useRef<boolean>(false);
	let userChangedValue = React.useRef<boolean>(false);
	let userChangedUOM = React.useRef<boolean>(false);
	let isInteractingWithPricingUOM = React.useRef<boolean>();
	let isInteractingWithQTYUOM = React.useRef<boolean>();

	const doc = useDocument();

	const query = doc && doc.querySelector?.bind(doc);
	const [displayQuantity, setDisplayQuantity] = React.useState<number | undefined>(quantityValue);
	// create state array to hold the messages
	const [message, setMessage] = React.useState<Array<any>>([]);

	// these items with ids will disrupt the debounce timeout then set deferred value
	const Ids = [`quantity-uom-dropdown-${id}`];

	let valueToDebounce = `${typeof displayQuantity !== "undefined" ? displayQuantity : ""}`;

	let debouncedQuantity = useDebouncerPlus(
		id,
		valueToDebounce,
		debounceTime,
		Ids,
		stateMessenger,
		!isRecalculate.current
	);

	// Start Local Debounce/Interval when Pub Messages from Pricing UOM component indicated user has interacted with PricingUOM
	const setSelectInteractionResult = (eventType: string) => {
		const selectLeaveEvents = ["blur", "change", "mouseout"];
		const selectEnterEvents = ["mousedown", "click", "focus"];

		let previousState = isInteractingWithPricingUOM.current;
		selectEnterEvents.includes(eventType) && (isInteractingWithPricingUOM.current = true);
		selectLeaveEvents.includes(eventType) && (isInteractingWithPricingUOM.current = false);

		if (!isInteractingWithPricingUOM.current && !!previousState && !defferChangeTrigger) {
			setDefferChangeTrigger(true);
		}
	};

	React.useEffect(() => {
		setQuantityUom(uomValue);
	}, [uomValue]);

	React.useEffect(() => {
		if (isMounted && isFocused) {
			(numberInputRef.current as HTMLInputElement)?.focus();
		}
	}, [isFocused]);

	/***
	 *  @desc  UseEffect [quantityValue]
	 *  This handles incoming (not typed in) quantityValue prop that has value of undefined
	 ***/

	React.useEffect(() => {
		if (isMounted && quantityValue === undefined && displayQuantity !== undefined) {
			setIsPopperDisplayed(false);
			handleQuantityOnChange("");
		} else if (quantityValue !== displayQuantity) {
			setDisplayQuantity(quantityValue);
		}
	}, [quantityValue]);

	/***
	 *  @desc UseEffect [displayQuantity]
	 *  handle displayQuantity has been changed, call validation Response callback
	 ***/

	React.useEffect(() => {
		if (isMounted && !isRecalculate.current) {
			let hasError = inputIsInError(displayQuantity);
			handleValidationResponse && handleValidationResponse(hasError, itemData.id);
		}
	}, [displayQuantity]);

	/***
	 *  @desc  handleQuantityOnChange()
	 *  Change Event Handler to Set displayQuantity state to numeric value or undefined
	 *  user has typed in a value.
	 ***/

	const handleQuantityOnChange = (value: string | number) => {
		const inputHasLength = `${value}`.length;
		let newValue = inputHasLength ? Number(value) : undefined;
		const { minimum, maximum } = inputConstraint;
		newValue =
			typeof newValue === "number"
				? Math.min(Math.max(newValue, minimum), maximum)
				: newValue;

		if (newValue !== displayQuantity) {
			isRecalculate.current = false;
			setDisplayQuantity(newValue);
		}
	};

	// `forceTrigger` path: validate, show and report the typed value at once, without clamping or
	// adjustment. An empty input becomes 0, which is an error, so `handleChange` is not called.
	const forceTriggerChangeHandler = (value: string | number) => {
		validateInput(value);
		let numericValue = Number(value);
		setDisplayQuantity(numericValue);
		handleValidationResponse && handleValidationResponse(isError.current, itemData?.id);
		!isError.current && handleChange(numericValue, quantityUom);
	};

	const publishMessage = (messageString: string, data: any, topic: any) => {
		/* note the use of setTimeout is required for publishing messages*/
		setTimeout(() => stateMessenger?.publish(messageString, data, topic), 10);
	};

	/**
	 * Messages from external components via pub sub
	 */
	React.useEffect(() => {
		let [messageString, value, dropDownId] = message;
		// determine is (external) select dropdown are being used
		messageString === "to-debounce" &&
			`${dropDownId}` === `${id}` &&
			setSelectInteractionResult(value);
	}, [message]);

	/***
	 *  @desc Hook: useCheckValueInterval
	 *   Deals with setting up a debounce/interval when user interacts with UOM dropdown or Pricing UOM  (has not typed in quantity value)
	 *   this does a check when timer runs out see if if needs to do the recalculation and ,ale onchange callback
	 *   which (normally handled by the input debounce), however if user then interacts with input or other dropdown
	 *   this timeout action is cancelled, since the uses the standard debounce functionality
	 ***/
	useCheckValueInterval(
		() => {
			// tell pricing uom were done debouncing
			publishMessage(`is-debouncing`, false, id);

			// has user has not started changing quantity  then trigger recalc & onchange callback
			if (!hasInputChanged() && !isInteractingWithPricingUOM.current) {
				userChangedValue.current = true;
				recalculateQuantityAndChange();
			}
			// kill this interval/debounce session
			setDefferChangeTrigger(false);
		},
		(count) => {
			if (isInteractingWithQTYUOM.current || isInteractingWithPricingUOM.current) {
				publishMessage(`is-debouncing`, false, id);
				setDefferChangeTrigger(false);
			}
		},
		defferChangeTrigger,
		debounceTime / 3,
		3
	);

	// The Interval Debouncing has started, so let external components know
	React.useEffect(() => {
		// this tells pricing uom dropdown we are now debouncing
		isMounted && defferChangeTrigger && publishMessage(`is-debouncing`, true, id);
	}, [defferChangeTrigger]);

	/***
	 *  @desc hasInputChanged() (a function, not an effect)
	 * compare state value and  DOM input element value
	 ***/
	const hasInputChanged = () => {
		let El = query && (query(`#quantity-uom-input-${id}`) as any);
		return `${displayQuantity}` !== `${El?.value || ""}`;
	};

	/***
	 *  @desc UseEffect[isSubmitted]
	 * Deal with isSubmitted Prop updated, validate input
	 ***/

	React.useEffect(() => {
		if (isMounted && isSubmitted) {
			validateInput(displayQuantity);
		}
	}, [isSubmitted]);

	/***
	 *  @desc  @UseEffect [debounce]
	 *  delay of incoming value, check to see if we need to recalculate input and redisplay it with new state
	 ***/
	React.useEffect(() => {
		if (!forceTrigger) {
			let legitValue =
				typeof displayQuantity !== "undefined" &&
				!isRecalculate.current &&
				(hasUserChangedValue ||
					(userChangedValue.current &&
						!!debouncedQuantity &&
						Number(debouncedQuantity) > 0));

			// Note: user ONLY changed UOM dropped down but did'nt change qty value
			// start debounce/interval
			let userOnlyChangedQtyUOM =
				!userChangedValue.current && userChangedUOM.current && !!displayQuantity;
			if (userOnlyChangedQtyUOM) {
				setDefferChangeTrigger(true);
			}

			if (isMounted && legitValue && !defferChangeTrigger) {
				recalculateQuantityAndChange();
			} else {
				// Reset Flags
				!!debouncedQuantity && (isRecalculate.current = false);
			}
		}
	}, [debouncedQuantity, quantityUom]);

	/***
	 *  @desc recalculateQuantityAndChange -   call recalculation function,show popper, the onChange Callback for this component
	 ***/
	const recalculateQuantityAndChange = () => {
		// bypass local calculation and directly call onchange
		if (disableLocalCalculation) {
			handleChange && handleChange(displayQuantity, quantityUom);

			// Reset Flags (important)
			userChangedValue.current = false;
			userChangedUOM.current = false;

			return;
		}
		let quantityToDisplay = recalculateQuantities
			? recalculateQuantity(displayQuantity)
			: displayQuantity;
		//show the popper and show new calculated number
		if (quantityToDisplay !== Number(displayQuantity)) {
			isRecalculate.current && setIsPopperDisplayed(true);
			setDisplayQuantity(quantityToDisplay);
		}
		(!!userChangedValue.current || hasUserChangedValue) &&
			handleChange &&
			handleChange(quantityToDisplay, quantityUom);
		// Reset Flags (important)
		userChangedValue.current = false;
		userChangedUOM.current = false;
	};

	/***
	 *  @desc recalculateQuantity - rounds up values to appropriated amount for particular uom value
	 ***/
	const recalculateQuantity = (quantity: any) => {
		const isNonZero = displayQuantity !== 0;
		isRecalculate.current = isNonZero;
		let updatedQuantity: number | undefined = undefined;
		if (isRecalculate.current) {
			/**
			 * Check that the user’s quantity corresponds to a full piece.
			 * If it does, then the `updatedQuantity` will yield the next quantity for an extra piece (user inputs 50lbs, updatedQty will be 100lbs).
			 * That’s not what we want: if the user inputs the quantity for a full piece, then don’t change it
			 * (user inputs 50lbs, it’s a full piece, leave it).
			 */
			let provisionalQty = updatedQuantityBasedOnQuantityUom(
				itemData as any,
				quantityUom as any,
				quantity - 1
			);
			updatedQuantity = updatedQuantityBasedOnQuantityUom(
				itemData as any,
				quantityUom as any,
				quantity
			);
			if (provisionalQty === quantity) {
				updatedQuantity = provisionalQty;
			}
		}
		return !!updatedQuantity ? updatedQuantity : quantity;
	};

	/***
	 *  @desc  UseEffect all validation response to set if table is valid
	 ***/

	React.useEffect(() => {
		window &&
			(window as any)?.setTimeout(() => {
				numberInputRef.current = document?.querySelector(
					`[data-testid=${inputDataTestId ?? "number-input-" + id}]`
				);
				let hasError = inputIsInError(displayQuantity);
				handleValidationResponse && handleValidationResponse(hasError, itemData.id);
			}, 10);
	}, [isMounted]);

	React.useEffect(() => {
		stateMessenger?.subscribe((msg: string, value: any, topic: any) =>
			setMessage([msg, value, topic])
		);

		setHasMounted(true);
	}, []);

	React.useEffect(() => {
		//set timer for popper to hide if displayed

		if (isPopperDisplayed) {
			setTimeout(() => {
				setIsPopperDisplayed(false);
			}, 2000);
		}
	}, [isPopperDisplayed]);

	/***
	 *  @desc getPopperColor()
	 *  Utility Function to set color for popper based on ControlType( primary | secondary | tertiary)
	 ***/

	const getPopperColor = (whichElType: "text" | "background" | "border") => {
		switch (type) {
			case "tertiary":
				return {
					text: text,
					background: white,
					border: lightGray,
				}[whichElType];
			case "primary":
			case "secondary":
			default:
				return {
					text: white,
					background: darkGray,
					border: lightGray,
				}[whichElType];
		}
	};

	const PopperContent = styled.div`
		width: ${popperWidth}px;
	`;

	const inputIsInError = (val: any) => {
		let isUndefined = !!(typeof val === "undefined");
		let isZero = !!(Number(val) === 0);

		return isZero || isUndefined;
	};

	/***
	 *  @desc validateInput()
	 *  Validator call back for input component
	 ***/
	const validateInput = (val: any): string => {
		isError.current = inputIsInError(val);
		isError.current && isSubmitted && setDisplayTrigger(!displayTrigger);
		return isError.current ? CONSTANTS[language as keyof typeof CONSTANTS].REQUIRED_FIELD : "";
	};

	return (
		<div className="qty-uom-component-root" data-testid={dataTestId}>
			<Flex className="qty-uom-component-column" direction="column">
				<Flex alignItems="flex-start">
					<FlexItem
						align-items="flex-start"
						className="qty-uom-component-row"
						css={{
							width: `${quantityInputWidth}${
								quantityInputWidth.toString().includes("%") ? "" : "px"
							}`,
							marginRight: 10,
							minWidth: isMobile ? 120 : "unset",
						}}
					>
						<NumberInput
							labelAddition={InputLabelAddition}
							id={`quantity-uom-input-${id}`}
							inputRef={quantityInputRef}
							className={`quantity-uom-input-${id}`}
							value={displayQuantity || ""}
							disabled={disabledQuantity !== undefined ? disabledQuantity : disabled}
							validation={validateInput}
							isValid={!isError.current}
							submitted={false}
							required={true}
							label={showLabel ? content.quantity : undefined}
							type={type}
							dataTestId={inputDataTestId ?? `number-input-${id}`}
							size={size}
							onBlur={(val) => {
								return isSubmitted && validateInput(val);
							}}
							// Marks the next settled value as a customer change. The pattern
							// `/\D^Backspace/` never matches (`^` after a consumed character), so
							// this handler blocks no key.
							onKeyDown={(e) => {
								userChangedValue.current = true;
								return e.key.match(/\D^Backspace/) && e.preventDefault();
							}}
							onChange={(e) => {
								if (forceTrigger) {
									forceTriggerChangeHandler(e.target.value);
								} else {
									const value = e.target.value;
									isSubmitted && validateInput(value);
									setIsPopperDisplayed(false);
									handleQuantityOnChange(value);
									setDefferChangeTrigger(false);
								}
							}}
							language={language}
						/>
					</FlexItem>
					<div
						css={{
							width: `${uomInputWidth}${
								uomInputWidth.toString().includes("%") ? "" : "px"
							}`,
						}}
					>
						<Dropdown
							onDropdownOpen={onDropdownOpen}
							labelAddition={DropDownLabelAddition}
							disabled={disabled || quantityUomOptions.length <= 1}
							label={showLabel ? content.uom : undefined}
							dataTestId={dropdownDataTestId}
							id={`quantity-uom-dropdown-${id}`}
							type={type}
							value={quantityUom}
							options={quantityUomOptions}
							onChange={(value) => {
								isInteractingWithQTYUOM.current = false;
								userChangedUOM.current = true;
								setQuantityUom(value);
								handleUOMChange && handleUOMChange(value);
							}}
							size={size}
							language={language}
						/>
					</div>
				</Flex>

				{numberInputRef.current && (
					<Popper
						{...{
							id: id,
							type: type,
							targetEl: numberInputRef.current,
							backColor: getPopperColor("background"),
							borderColor: getPopperColor("border"),
							offsetY: 4,
							displayTime: popperDisplayTime,
							placement: "bottom",
							isDisplaying: isPopperDisplayed,
							handlePopperTimedOut: () => setIsPopperDisplayed(false),
							onClick: () => {
								setIsPopperDisplayed(false);
							},
						}}
					>
						<PopperContent>
							<Alert size="sm" severity={"info"}>
								<Typography color={getPopperColor("text")}>
									{content.quantityRecalculatedNotify}
								</Typography>
							</Alert>
						</PopperContent>
					</Popper>
				)}
			</Flex>
		</div>
	);
};

// Content labels model
/** Label keys for {@link withLanguage}. */
export interface QuantityUOMLabels {
	quantity: string;
	uom: string;
	quantityRecalculatedNotify: string;
}

/**
 * Labels for {@link QuantityUOMV2} in en / es / fr. `quantity` and `uom` show only with
 * `showLabel`; `quantityRecalculatedNotify` is the popper text after an adjustment.
 */
export const withLanguage: LanguageContent<QuantityUOMLabels> = {
	en: {
		quantity: "Quantity",
		uom: "UOM",
		quantityRecalculatedNotify: "Values have been updated",
	},
	es: {
		quantity: "Cantidad",
		uom: "UOM",
		quantityRecalculatedNotify: "Los números han sido actualizados",
	},
	fr: {
		quantity: "Quantité",
		uom: "UOM",
		quantityRecalculatedNotify: "Les chiffres ont été mis à jour",
	},
};
